import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  X,
  Plus,
  Trash2,
  Edit,
  Save,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Type,
  ListChecks,
  Table as TableIcon,
  Calculator
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { generateAttributeFieldName, NUMBER_FORMAT_PRESETS, formatNumber } from './utils';
import FormulaBuilder from './FormulaBuilder';

const ATTRIBUTE_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'choice', label: 'Choice' },
  { value: 'table', label: 'Table' }
];

const CustomAttributesConfig = ({
  fields = [],
  entityName,
  onFieldsUpdated,
  onClose
}) => {
  const { hasRole } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();
  const { t, i18n } = useTranslation();
  const isThai = i18n?.language?.toLowerCase().startsWith('th') || false;

  const [localFields, setLocalFields] = useState([...fields]);
  const [editingField, setEditingField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddField, setShowAddField] = useState(false);

  // New field form state
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldNameTH, setNewFieldNameTH] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [choiceOptionsInput, setChoiceOptionsInput] = useState('');
  const [tableColumnsInput, setTableColumnsInput] = useState('');
  
  // Number format state
  const [numberFormatPreset, setNumberFormatPreset] = useState('none');
  const [numberFormatCustom, setNumberFormatCustom] = useState({
    decimals: 2,
    useThousandsSeparator: true,
    prefix: '',
    suffix: ''
  });
  
  // Column editing state
  const [editingColumn, setEditingColumn] = useState(null); // { fieldName, columnIndex, column }
  const [showFormulaBuilder, setShowFormulaBuilder] = useState(false);
  
  // Column number format state
  const [columnNumberFormatPreset, setColumnNumberFormatPreset] = useState('none');
  const [columnNumberFormatCustom, setColumnNumberFormatCustom] = useState({
    decimals: 2,
    useThousandsSeparator: true,
    prefix: '',
    suffix: ''
  });

  useEffect(() => {
    setLocalFields([...fields]);
  }, [fields]);

  const getFieldLabel = (field) => {
    if (!field) return '';
    if (isThai) {
      return field.displayNameTH || field.displayName || field.name;
    }
    return field.displayName || field.displayNameTH || field.name;
  };

  const handleDeleteField = async (fieldName) => {
    const fieldToDelete = fields.find(f => f.name === fieldName);
    const fieldLabel = getFieldLabel(fieldToDelete);
    
    if (!window.confirm(`Are you sure you want to delete field "${fieldLabel}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setIsSaving(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      console.log(`🗑️ Deleting custom field "${fieldName}" from ${entityName}`);

      const response = await fetch(`/api/tenant-entity-config/${entityName}/custom-field/${encodeURIComponent(fieldName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json().catch(() => ({}));
      
      if (!response.ok) {
        console.error('❌ Delete field failed:', result);
        if (response.status === 404) {
          throw new Error(`Field "${fieldLabel}" not found. It may have already been deleted or the field name doesn't match.`);
        }
        throw new Error(result.message || 'Failed to delete field');
      }

      console.log('✅ Field deleted successfully:', result);
      toast.success('Field deleted successfully');
      if (onFieldsUpdated) {
        await onFieldsUpdated();
      }
    } catch (error) {
      console.error('Error deleting field:', error);
      toast.error(error.message || 'Failed to delete field');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditField = (field) => {
    setEditingField({ ...field });
    setNewFieldType(field.metadata?.attributeType || field.ui?.inputType || field.dataType || 'text');
    
    // Load number format
    const numberFormat = field?.metadata?.numberFormat || field?.ui?.numberFormat;
    if (numberFormat && numberFormat.preset) {
      setNumberFormatPreset(numberFormat.preset);
      if (numberFormat.preset === 'custom') {
        setNumberFormatCustom({
          decimals: numberFormat.decimals !== undefined ? numberFormat.decimals : 2,
          useThousandsSeparator: numberFormat.useThousandsSeparator !== false,
          prefix: numberFormat.prefix || '',
          suffix: numberFormat.suffix || ''
        });
      }
    } else {
      setNumberFormatPreset('none');
      setNumberFormatCustom({
        decimals: 2,
        useThousandsSeparator: true,
        prefix: '',
        suffix: ''
      });
    }
    
    // Load choice options
    if (field.validation?.enum || field.ui?.options) {
      const options = field.validation?.enum || field.ui?.options || [];
      setChoiceOptionsInput(options.map(opt => {
        if (typeof opt === 'string') return opt;
        return `${opt.label || opt.value} | ${opt.value}${opt.labelTH ? ` | ${opt.labelTH}` : ''}`;
      }).join('\n'));
    } else {
      setChoiceOptionsInput('');
    }

    // Load table columns
    if (field.columns || field.ui?.columns) {
      const columns = field.columns || field.ui?.columns || [];
      setTableColumnsInput(columns.map(col => {
        return `${col.label || col.key} | ${col.type || 'text'}`;
      }).join('\n'));
    } else {
      setTableColumnsInput('');
    }
  };

  const handleSaveFieldEdit = async () => {
    if (!editingField) return;

    try {
      setIsSaving(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      // Parse choice options
      let choiceOptions = [];
      if (newFieldType === 'choice' && choiceOptionsInput.trim()) {
        choiceOptions = choiceOptionsInput.split('\n')
          .filter(line => line.trim())
          .map(line => {
            const parts = line.split('|').map(p => p.trim());
            const option = {
              value: parts[1] || parts[0].toLowerCase().replace(/\s+/g, '_'),
              label: parts[0] || parts[1]
            };
            if (parts[2]) option.labelTH = parts[2];
            return option;
          });
      }

      // Parse table columns
      let tableColumns = [];
      if (newFieldType === 'table' && tableColumnsInput.trim()) {
        tableColumns = tableColumnsInput.split('\n')
          .filter(line => line.trim())
          .map((line, idx) => {
            const parts = line.split('|').map(p => p.trim());
            const label = parts[0] || `Column ${idx + 1}`;
            const type = parts[1] || 'text';
            const key = label.toLowerCase().replace(/\s+/g, '_') || `col_${idx}`;
            
            const column = {
              key,
              label,
              type
            };
            
            // Handle calculated columns
            if (type === 'calculated') {
              column.formula = parts[2] || '';
              column.showTotal = parts[3]?.toLowerCase() === 'true';
            } else {
              // Handle showTotal for regular number columns
              if (parts[2]?.toLowerCase() === 'true' || parts[2]?.toLowerCase() === 'false') {
                column.showTotal = parts[2]?.toLowerCase() === 'true';
              }
            }
            
            return column;
          });
      }

      // Build number format config
      let numberFormat = null;
      if (newFieldType === 'number') {
        if (numberFormatPreset && numberFormatPreset !== 'none') {
          if (numberFormatPreset === 'custom') {
            numberFormat = {
              preset: 'custom',
              decimals: numberFormatCustom.decimals,
              useThousandsSeparator: numberFormatCustom.useThousandsSeparator,
              prefix: numberFormatCustom.prefix,
              suffix: numberFormatCustom.suffix
            };
          } else {
            numberFormat = { preset: numberFormatPreset };
          }
        }
      }

      // Build field config update
      const fieldConfig = {
        displayName: editingField.displayName || newFieldName,
        displayNameTH: editingField.displayNameTH || newFieldNameTH,
        dataType: newFieldType === 'choice' ? 'string' : newFieldType,
        metadata: {
          ...editingField.metadata,
          attributeType: newFieldType,
          ...(numberFormat && { numberFormat })
        },
        ui: {
          ...editingField.ui,
          inputType: newFieldType === 'choice' ? 'select' : newFieldType,
          ...(numberFormat && { numberFormat })
        }
      };

      if (newFieldType === 'choice' && choiceOptions.length > 0) {
        fieldConfig.validation = {
          ...editingField.validation,
          enum: choiceOptions.map(opt => opt.value)
        };
        fieldConfig.ui.options = choiceOptions;
      }

      if (newFieldType === 'table' && tableColumns.length > 0) {
        fieldConfig.columns = tableColumns;
        fieldConfig.ui = {
          ...fieldConfig.ui,
          columns: tableColumns
        };
      }

      const response = await fetch(`/api/tenant-entity-config/${entityName}/custom-field/${editingField.name}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fieldConfig
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update field');
      }

      toast.success('Field updated successfully');
      setEditingField(null);
      if (onFieldsUpdated) {
        await onFieldsUpdated();
      }
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error(error.message || 'Failed to update field');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMoveColumn = async (fieldName, columnIndex, direction) => {
    const field = localFields.find(f => f.name === fieldName);
    if (!field || field.metadata?.attributeType !== 'table') return;

    const columns = field.columns || field.ui?.columns || [];
    if (
      (direction === 'up' && columnIndex === 0) ||
      (direction === 'down' && columnIndex === columns.length - 1)
    ) {
      return;
    }

    const newColumns = [...columns];
    const newIndex = direction === 'up' ? columnIndex - 1 : columnIndex + 1;
    [newColumns[columnIndex], newColumns[newIndex]] = [newColumns[newIndex], newColumns[columnIndex]];

    const updatedFields = localFields.map(f => {
      if (f.name === fieldName) {
        return {
          ...f,
          columns: newColumns,
          ui: {
            ...f.ui,
            columns: newColumns
          }
        };
      }
      return f;
    });

    setLocalFields(updatedFields);

    // Save to backend
    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');
      const updatedField = updatedFields.find(f => f.name === fieldName);

      const response = await fetch(`/api/tenant-entity-config/${entityName}/custom-field/${fieldName}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fieldConfig: {
            ...updatedField,
            columns: newColumns,
            ui: {
              ...updatedField.ui,
              columns: newColumns
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update column order');
      }

      if (onFieldsUpdated) {
        await onFieldsUpdated();
      }
    } catch (error) {
      console.error('Error updating column order:', error);
      toast.error('Failed to update column order');
      // Revert on error
      setLocalFields([...fields]);
    }
  };

  const handleUpdateColumnName = async (fieldName, columnIndex, newName) => {
    const field = localFields.find(f => f.name === fieldName);
    if (!field || field.metadata?.attributeType !== 'table') return;

    const columns = field.columns || field.ui?.columns || [];
    const updatedColumns = [...columns];
    updatedColumns[columnIndex] = {
      ...updatedColumns[columnIndex],
      label: newName
    };

    const updatedFields = localFields.map(f => {
      if (f.name === fieldName) {
        return {
          ...f,
          columns: updatedColumns,
          ui: {
            ...f.ui,
            columns: updatedColumns
          }
        };
      }
      return f;
    });

    setLocalFields(updatedFields);

    // Save to backend
    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');
      const updatedField = updatedFields.find(f => f.name === fieldName);

      const response = await fetch(`/api/tenant-entity-config/${entityName}/custom-field/${fieldName}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fieldConfig: {
            ...updatedField,
            columns: updatedColumns,
            ui: {
              ...updatedField.ui,
              columns: updatedColumns
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update column');
      }

      toast.success('Column updated successfully');
      if (onFieldsUpdated) {
        await onFieldsUpdated();
      }
    } catch (error) {
      console.error('Error updating column:', error);
      toast.error('Failed to update column');
    }
  };

  const handleEditColumn = (fieldName, columnIndex) => {
    const field = localFields.find(f => f.name === fieldName);
    if (!field || field.metadata?.attributeType !== 'table') return;

    const columns = field.columns || field.ui?.columns || [];
    const column = columns[columnIndex];
    
    // Load column number format for number/calculated columns
    const columnNumberFormat = column?.numberFormat;
    if (column?.type === 'number' || column?.type === 'calculated') {
      if (columnNumberFormat && columnNumberFormat.preset) {
        setColumnNumberFormatPreset(columnNumberFormat.preset);
        if (columnNumberFormat.preset === 'custom') {
          setColumnNumberFormatCustom({
            decimals: columnNumberFormat.decimals !== undefined ? columnNumberFormat.decimals : 2,
            useThousandsSeparator: columnNumberFormat.useThousandsSeparator !== false,
            prefix: columnNumberFormat.prefix || '',
            suffix: columnNumberFormat.suffix || ''
          });
        }
      } else {
        setColumnNumberFormatPreset('none');
        setColumnNumberFormatCustom({
          decimals: 2,
          useThousandsSeparator: true,
          prefix: '',
          suffix: ''
        });
      }
    } else {
      setColumnNumberFormatPreset('none');
      setColumnNumberFormatCustom({
        decimals: 2,
        useThousandsSeparator: true,
        prefix: '',
        suffix: ''
      });
    }
    
    setEditingColumn({
      fieldName,
      columnIndex,
      originalColumn: { ...column },
      column: { ...column }
    });
  };

  const handleDeleteColumn = async (fieldName, columnIndex) => {
    const field = localFields.find(f => f.name === fieldName);
    if (!field || field.metadata?.attributeType !== 'table') return;

    const columns = field.columns || field.ui?.columns || [];
    const column = columns[columnIndex];

    if (!window.confirm(`Are you sure you want to delete column "${column.label}"?\n\nThis will remove all data in this column from existing records.`)) {
      return;
    }

    const updatedColumns = columns.filter((_, idx) => idx !== columnIndex);

    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/tenant-entity-config/${entityName}/custom-field/${fieldName}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fieldConfig: {
            columns: updatedColumns,
            ui: {
              columns: updatedColumns
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete column');
      }

      toast.success('Column deleted successfully');
      if (onFieldsUpdated) {
        await onFieldsUpdated();
      }
    } catch (error) {
      console.error('Error deleting column:', error);
      toast.error('Failed to delete column');
    }
  };

  const handleAddColumn = async (fieldName) => {
    const field = localFields.find(f => f.name === fieldName);
    if (!field || field.metadata?.attributeType !== 'table') return;

    const columns = field.columns || field.ui?.columns || [];
    const newColumn = {
      key: `col_${columns.length + 1}`,
      label: `Column ${columns.length + 1}`,
      type: 'text'
    };

    const updatedColumns = [...columns, newColumn];

    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/tenant-entity-config/${entityName}/custom-field/${fieldName}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fieldConfig: {
            columns: updatedColumns,
            ui: {
              columns: updatedColumns
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add column');
      }

      toast.success('Column added successfully');
      if (onFieldsUpdated) {
        await onFieldsUpdated();
      }
    } catch (error) {
      console.error('Error adding column:', error);
      toast.error('Failed to add column');
    }
  };

  const handleSaveColumnEdit = async () => {
    if (!editingColumn) return;

    const { fieldName, columnIndex, column, originalColumn } = editingColumn;
    const field = localFields.find(f => f.name === fieldName);
    if (!field || field.metadata?.attributeType !== 'table') return;

    // Check if column name changed
    const nameChanged = column.label !== originalColumn.label;
    const keyChanged = column.key !== originalColumn.key;
    
    if (nameChanged || keyChanged) {
      const confirmMessage = `Are you sure you want to change column?\n\n` +
        `From: "${originalColumn.label}" (${originalColumn.key})\n` +
        `To: "${column.label}" (${column.key})\n\n` +
        `This will update the column definition but preserve existing data under the current key "${originalColumn.key}".`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    const columns = field.columns || field.ui?.columns || [];
    const updatedColumns = [...columns];
    
    // Build number format config for number/calculated columns
    let numberFormat = null;
    if (column.type === 'number' || column.type === 'calculated') {
      if (columnNumberFormatPreset && columnNumberFormatPreset !== 'none') {
        if (columnNumberFormatPreset === 'custom') {
          numberFormat = {
            preset: 'custom',
            decimals: columnNumberFormatCustom.decimals,
            useThousandsSeparator: columnNumberFormatCustom.useThousandsSeparator,
            prefix: columnNumberFormatCustom.prefix,
            suffix: columnNumberFormatCustom.suffix
          };
        } else {
          numberFormat = { preset: columnNumberFormatPreset };
        }
      }
    }
    
    updatedColumns[columnIndex] = { 
      ...column,
      ...(numberFormat && { numberFormat })
    };

    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/tenant-entity-config/${entityName}/custom-field/${fieldName}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fieldConfig: {
            columns: updatedColumns,
            ui: {
              columns: updatedColumns
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update column');
      }

      toast.success(`Column updated successfully${nameChanged ? '. Note: Existing data is preserved under key "' + originalColumn.key + '"' : ''}`);
      setEditingColumn(null);
      if (onFieldsUpdated) {
        await onFieldsUpdated();
      }
    } catch (error) {
      console.error('Error updating column:', error);
      toast.error('Failed to update column');
    }
  };

  const handleAddField = async () => {
    if (!newFieldName.trim()) {
      toast.error('Field name is required');
      return;
    }

    try {
      setIsSaving(true);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const fieldName = generateAttributeFieldName(newFieldName);

      // Parse choice options
      let choiceOptions = [];
      if (newFieldType === 'choice' && choiceOptionsInput.trim()) {
        choiceOptions = choiceOptionsInput.split('\n')
          .filter(line => line.trim())
          .map(line => {
            const parts = line.split('|').map(p => p.trim());
            return {
              value: parts[1] || parts[0].toLowerCase().replace(/\s+/g, '_'),
              label: parts[0] || parts[1],
              ...(parts[2] && { labelTH: parts[2] })
            };
          });
      }

      // Parse table columns
      let tableColumns = [];
      if (newFieldType === 'table' && tableColumnsInput.trim()) {
        tableColumns = tableColumnsInput.split('\n')
          .filter(line => line.trim())
          .map((line, idx) => {
            const parts = line.split('|').map(p => p.trim());
            const label = parts[0] || `Column ${idx + 1}`;
            const type = parts[1] || 'text';
            const key = label.toLowerCase().replace(/\s+/g, '_') || `col_${idx}`;
            
            const column = {
              key,
              label,
              type
            };
            
            // Handle calculated columns
            if (type === 'calculated') {
              column.formula = parts[2] || '';
              column.showTotal = parts[3]?.toLowerCase() === 'true';
            } else {
              // Handle showTotal for regular number columns
              if (parts[2]?.toLowerCase() === 'true' || parts[2]?.toLowerCase() === 'false') {
                column.showTotal = parts[2]?.toLowerCase() === 'true';
              }
            }
            
            return column;
          });
      }

      // Build number format config
      let numberFormat = null;
      if (newFieldType === 'number') {
        if (numberFormatPreset && numberFormatPreset !== 'none') {
          if (numberFormatPreset === 'custom') {
            numberFormat = {
              preset: 'custom',
              decimals: numberFormatCustom.decimals,
              useThousandsSeparator: numberFormatCustom.useThousandsSeparator,
              prefix: numberFormatCustom.prefix,
              suffix: numberFormatCustom.suffix
            };
          } else {
            numberFormat = { preset: numberFormatPreset };
          }
        }
      }

      const fieldConfig = {
        displayName: newFieldName,
        displayNameTH: newFieldNameTH || '',
        dataType: newFieldType === 'choice' ? 'string' : newFieldType,
        metadata: {
          isCustomAttribute: true,
          attributeType: newFieldType,
          attributeSource: 'attributes',
          tenantCode: tenantCode,
          tenantOnly: true,
          ...(numberFormat && { numberFormat })
        },
        storage: {
          type: 'attributes',
          path: fieldName
        },
        ui: {
          inputType: newFieldType === 'choice' ? 'select' : newFieldType,
          showInList: true,
          showInForm: true,
          showInDetail: true,
          ...(numberFormat && { numberFormat })
        }
      };

      if (newFieldType === 'choice' && choiceOptions.length > 0) {
        fieldConfig.validation = {
          enum: choiceOptions.map(opt => opt.value)
        };
        fieldConfig.ui.options = choiceOptions;
      }

      if (newFieldType === 'table' && tableColumns.length > 0) {
        fieldConfig.columns = tableColumns;
        fieldConfig.ui.columns = tableColumns;
      }

      const response = await fetch(`/api/tenant-entity-config/${entityName}/custom-field`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fieldName,
          fieldConfig
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add field');
      }

      toast.success('Field added successfully');
      setShowAddField(false);
      setNewFieldName('');
      setNewFieldNameTH('');
      setNewFieldType('text');
      setChoiceOptionsInput('');
      setTableColumnsInput('');
      
      if (onFieldsUpdated) {
        await onFieldsUpdated();
      }
    } catch (error) {
      console.error('Error adding field:', error);
      toast.error(error.message || 'Failed to add field');
    } finally {
      setIsSaving(false);
    }
  };

  if (!hasRole(['Manager', 'Admin'])) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        // Close modal only if clicking the overlay, not the content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => {
          // Prevent clicks inside modal from closing it
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Settings className="h-6 w-6 text-teal-600 dark:text-teal-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Configure Custom Attributes
            </h2>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Field Button */}
          <div className="mb-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAddField(!showAddField);
              }}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              <span>Add New Field</span>
            </button>
          </div>

          {/* Add Field Form */}
          {showAddField && (
            <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Add New Field</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Field Label (English) *
                  </label>
                  <input
                    type="text"
                    value={newFieldName}
                    onChange={(e) => setNewFieldName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="e.g., Contract Start Date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Field Label (Thai)
                  </label>
                  <input
                    type="text"
                    value={newFieldNameTH}
                    onChange={(e) => setNewFieldNameTH(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="e.g., วันเริ่มสัญญา"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Field Type
                  </label>
                  <select
                    value={newFieldType}
                    onChange={(e) => {
                      setNewFieldType(e.target.value);
                      if (e.target.value !== 'choice') setChoiceOptionsInput('');
                      if (e.target.value !== 'table') setTableColumnsInput('');
                      if (e.target.value !== 'number') {
                        setNumberFormatPreset('none');
                        setNumberFormatCustom({
                          decimals: 2,
                          useThousandsSeparator: true,
                          prefix: '',
                          suffix: ''
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {ATTRIBUTE_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                {newFieldType === 'choice' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Choice Options (one per line: Label | value | Thai label)
                    </label>
                    <textarea
                      value={choiceOptionsInput}
                      onChange={(e) => setChoiceOptionsInput(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows={4}
                      placeholder="Standard | standard | มาตรฐาน"
                    />
                  </div>
                )}
                {newFieldType === 'number' && (
                  <div className="space-y-3 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Number Format
                    </label>
                    
                    {/* Quick Format Presets */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                        Quick Format (presets)
                      </label>
                      <select
                        value={numberFormatPreset}
                        onChange={(e) => setNumberFormatPreset(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        {Object.values(NUMBER_FORMAT_PRESETS).map(preset => (
                          <option key={preset.value} value={preset.value}>
                            {preset.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Custom Format Options (shown when custom is selected) */}
                    {numberFormatPreset === 'custom' && (
                      <div className="space-y-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Decimal Places
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="10"
                              value={numberFormatCustom.decimals}
                              onChange={(e) => setNumberFormatCustom({
                                ...numberFormatCustom,
                                decimals: parseInt(e.target.value) || 0
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Thousands Separator
                            </label>
                            <label className="flex items-center h-10 px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                              <input
                                type="checkbox"
                                checked={numberFormatCustom.useThousandsSeparator}
                                onChange={(e) => setNumberFormatCustom({
                                  ...numberFormatCustom,
                                  useThousandsSeparator: e.target.checked
                                })}
                                className="h-4 w-4 text-teal-600 rounded focus:ring-teal-500"
                              />
                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use comma separator</span>
                            </label>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Prefix (e.g., $, ฿)
                            </label>
                            <input
                              type="text"
                              value={numberFormatCustom.prefix}
                              onChange={(e) => setNumberFormatCustom({
                                ...numberFormatCustom,
                                prefix: e.target.value
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                              placeholder="e.g., $, ฿"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              Suffix (e.g., %, kg)
                            </label>
                            <input
                              type="text"
                              value={numberFormatCustom.suffix}
                              onChange={(e) => setNumberFormatCustom({
                                ...numberFormatCustom,
                                suffix: e.target.value
                              })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                              placeholder="e.g., %, kg"
                            />
                          </div>
                        </div>
                        
                        {/* Preview */}
                        <div className="pt-2">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Preview
                          </label>
                          <div className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white font-mono">
                            {(() => {
                              const testValue = 12345.6789;
                              let formatted = testValue.toFixed(numberFormatCustom.decimals);
                              if (numberFormatCustom.useThousandsSeparator) {
                                const parts = formatted.split('.');
                                parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                formatted = parts.join('.');
                              }
                              return `${numberFormatCustom.prefix}${formatted}${numberFormatCustom.suffix}`;
                            })()}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Preview for presets */}
                    {numberFormatPreset !== 'custom' && numberFormatPreset !== 'none' && (
                      <div className="pt-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Preview
                        </label>
                        <div className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white font-mono">
                          {(() => {
                            const preset = NUMBER_FORMAT_PRESETS[numberFormatPreset];
                            if (!preset) return '12345.67';
                            const testValue = 12345.6789;
                            let formatted = testValue.toFixed(preset.decimals);
                            if (preset.useThousandsSeparator) {
                              const parts = formatted.split('.');
                              parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                              formatted = parts.join('.');
                            }
                            return `${preset.prefix}${formatted}${preset.suffix}`;
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {newFieldType === 'table' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Table Columns (one per line: Label | Type)
                    </label>
                    <textarea
                      value={tableColumnsInput}
                      onChange={(e) => setTableColumnsInput(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      rows={4}
                      placeholder="Item Name | text"
                    />
                  </div>
                )}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAddField(false);
                      setNewFieldName('');
                      setNewFieldNameTH('');
                      setNewFieldType('text');
                      setChoiceOptionsInput('');
                      setTableColumnsInput('');
                      setNumberFormatPreset('none');
                      setNumberFormatCustom({
                        decimals: 2,
                        useThousandsSeparator: true,
                        prefix: '',
                        suffix: ''
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddField();
                    }}
                    disabled={isSaving}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Add Field'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Fields List */}
          <div className="space-y-4">
            {localFields.map((field) => {
              const isEditing = editingField?.name === field.name;
              const fieldType = field.metadata?.attributeType || field.ui?.inputType || field.dataType || 'text';
              const columns = field.columns || field.ui?.columns || [];

              return (
                <div
                  key={field.name}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800"
                >
                  {isEditing ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100">Editing: {getFieldLabel(field)}</h4>
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveFieldEdit();
                            }}
                            disabled={isSaving}
                            className="px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 text-sm"
                          >
                            <Save className="h-4 w-4 inline mr-1" />
                            Save
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingField(null);
                            }}
                            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Field Type
                          </label>
                          <select
                            value={newFieldType}
                            onChange={(e) => {
                              setNewFieldType(e.target.value);
                              if (e.target.value !== 'choice') setChoiceOptionsInput('');
                              if (e.target.value !== 'table') setTableColumnsInput('');
                              if (e.target.value !== 'number') {
                                setNumberFormatPreset('none');
                                setNumberFormatCustom({
                                  decimals: 2,
                                  useThousandsSeparator: true,
                                  prefix: '',
                                  suffix: ''
                                });
                              }
                            }}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            {ATTRIBUTE_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        {newFieldType === 'choice' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Choice Options
                            </label>
                            <textarea
                              value={choiceOptionsInput}
                              onChange={(e) => setChoiceOptionsInput(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              rows={4}
                            />
                          </div>
                        )}
                        {newFieldType === 'number' && (
                          <div className="space-y-3 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Number Format
                            </label>
                            
                            {/* Quick Format Presets */}
                            <div>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                Quick Format (presets)
                              </label>
                              <select
                                value={numberFormatPreset}
                                onChange={(e) => setNumberFormatPreset(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                              >
                                {Object.values(NUMBER_FORMAT_PRESETS).map(preset => (
                                  <option key={preset.value} value={preset.value}>
                                    {preset.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Custom Format Options (shown when custom is selected) */}
                            {numberFormatPreset === 'custom' && (
                              <div className="space-y-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                      Decimal Places
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="10"
                                      value={numberFormatCustom.decimals}
                                      onChange={(e) => setNumberFormatCustom({
                                        ...numberFormatCustom,
                                        decimals: parseInt(e.target.value) || 0
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                      Thousands Separator
                                    </label>
                                    <label className="flex items-center h-10 px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                                      <input
                                        type="checkbox"
                                        checked={numberFormatCustom.useThousandsSeparator}
                                        onChange={(e) => setNumberFormatCustom({
                                          ...numberFormatCustom,
                                          useThousandsSeparator: e.target.checked
                                        })}
                                        className="h-4 w-4 text-teal-600 rounded focus:ring-teal-500"
                                      />
                                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use comma separator</span>
                                    </label>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                      Prefix (e.g., $, ฿)
                                    </label>
                                    <input
                                      type="text"
                                      value={numberFormatCustom.prefix}
                                      onChange={(e) => setNumberFormatCustom({
                                        ...numberFormatCustom,
                                        prefix: e.target.value
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      placeholder="e.g., $, ฿"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                      Suffix (e.g., %, kg)
                                    </label>
                                    <input
                                      type="text"
                                      value={numberFormatCustom.suffix}
                                      onChange={(e) => setNumberFormatCustom({
                                        ...numberFormatCustom,
                                        suffix: e.target.value
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      placeholder="e.g., %, kg"
                                    />
                                  </div>
                                </div>
                                
                                {/* Preview */}
                                <div className="pt-2">
                                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                    Preview
                                  </label>
                                  <div className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white font-mono">
                                    {(() => {
                                      const preset = NUMBER_FORMAT_PRESETS.custom;
                                      const formatConfig = {
                                        preset: 'custom',
                                        decimals: numberFormatCustom.decimals,
                                        useThousandsSeparator: numberFormatCustom.useThousandsSeparator,
                                        prefix: numberFormatCustom.prefix,
                                        suffix: numberFormatCustom.suffix
                                      };
                                      // Import formatNumber function inline (we'll need to import it properly)
                                      const testValue = 12345.6789;
                                      let formatted = testValue.toFixed(numberFormatCustom.decimals);
                                      if (numberFormatCustom.useThousandsSeparator) {
                                        const parts = formatted.split('.');
                                        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                        formatted = parts.join('.');
                                      }
                                      return `${numberFormatCustom.prefix}${formatted}${numberFormatCustom.suffix}`;
                                    })()}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Preview for presets */}
                            {numberFormatPreset !== 'custom' && numberFormatPreset !== 'none' && (
                              <div className="pt-2">
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                  Preview
                                </label>
                                <div className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white font-mono">
                                  {(() => {
                                    const preset = NUMBER_FORMAT_PRESETS[numberFormatPreset];
                                    if (!preset) return '12345.67';
                                    const testValue = 12345.6789;
                                    let formatted = testValue.toFixed(preset.decimals);
                                    if (preset.useThousandsSeparator) {
                                      const parts = formatted.split('.');
                                      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                      formatted = parts.join('.');
                                    }
                                    return `${preset.prefix}${formatted}${preset.suffix}`;
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {newFieldType === 'table' && (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Table Columns
                              </label>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddColumn(field.name);
                                }}
                                className="inline-flex items-center space-x-1 px-2 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700"
                              >
                                <Plus className="h-3 w-3" />
                                <span>Add Column</span>
                              </button>
                            </div>
                            <div className="space-y-2">
                              {columns.map((col, idx) => (
                                <div key={idx} className="flex items-center space-x-2 p-2 border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900">
                                  <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
                                      {col.label}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {col.type === 'calculated' ? `Calculated: ${col.formula || 'No formula'}` : col.type}
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditColumn(field.name, idx);
                                    }}
                                    className="p-1 text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                                    title="Edit column"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMoveColumn(field.name, idx, 'up');
                                    }}
                                    disabled={idx === 0}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                    title="Move up"
                                  >
                                    <ArrowUp className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMoveColumn(field.name, idx, 'down');
                                    }}
                                    disabled={idx === columns.length - 1}
                                    className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                    title="Move down"
                                  >
                                    <ArrowDown className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteColumn(field.name, idx);
                                    }}
                                    className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                    title="Delete column"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            {fieldType === 'choice' && <ListChecks className="h-4 w-4 text-gray-400" />}
                            {fieldType === 'table' && <TableIcon className="h-4 w-4 text-gray-400" />}
                            {!['choice', 'table'].includes(fieldType) && <Type className="h-4 w-4 text-gray-400" />}
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {getFieldLabel(field)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({fieldType})
                            </span>
                            {/* Quick Edit Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(field);
                              }}
                              className="p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                              title="Edit field"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </div>
                          {fieldType === 'table' && columns.length > 0 && (
                            <div className="mt-2">
                              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                <strong>{columns.length}</strong> columns:
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                {columns.map((col, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded">
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {col.label} 
                                      <span className="text-gray-400 ml-1">({col.type})</span>
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditColumn(field.name, idx);
                                      }}
                                      className="ml-2 p-0.5 text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                                      title="Edit column"
                                    >
                                      <Edit className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteField(field.name);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Delete field"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {localFields.length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              No custom attribute fields configured yet.
            </p>
          )}
        </div>
      </div>

      {/* Column Edit Modal */}
      {editingColumn && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditingColumn(null);
              setShowFormulaBuilder(false);
            }
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Edit className="h-5 w-5 text-teal-600 dark:text-teal-400" />
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  Edit Column
                </h3>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingColumn(null);
                  setShowFormulaBuilder(false);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Column Label *
                </label>
                <input
                  type="text"
                  value={editingColumn.column.label}
                  onChange={(e) => {
                    setEditingColumn({
                      ...editingColumn,
                      column: {
                        ...editingColumn.column,
                        label: e.target.value,
                        key: e.target.value.toLowerCase().replace(/\s+/g, '_')
                      }
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Product Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Column Key (auto-generated)
                </label>
                <input
                  type="text"
                  value={editingColumn.column.key}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400"
                  title="Column key is auto-generated from the label"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  This is the internal key used to store data. It's auto-generated from the label.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Column Type *
                </label>
                <select
                  value={editingColumn.column.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setEditingColumn({
                      ...editingColumn,
                      column: {
                        ...editingColumn.column,
                        type: newType,
                        ...(newType !== 'calculated' && { formula: undefined }),
                        ...(newType !== 'number' && { numberFormat: undefined })
                      }
                    });
                    if (newType === 'calculated') {
                      setShowFormulaBuilder(true);
                    }
                    if (newType !== 'number') {
                      setColumnNumberFormatPreset('none');
                      setColumnNumberFormatCustom({
                        decimals: 2,
                        useThousandsSeparator: true,
                        prefix: '',
                        suffix: ''
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="calculated">Calculated</option>
                </select>
              </div>

              {editingColumn.column.type === 'calculated' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Formula *
                    </label>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowFormulaBuilder(true);
                      }}
                      className="inline-flex items-center space-x-1 px-2 py-1 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                    >
                      <Calculator className="h-3 w-3" />
                      <span>Open Formula Builder</span>
                    </button>
                  </div>
                  
                  <input
                    type="text"
                    value={editingColumn.column.formula || ''}
                    onChange={(e) => {
                      setEditingColumn({
                        ...editingColumn,
                        column: {
                          ...editingColumn.column,
                          formula: e.target.value
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                    placeholder="e.g., {qty} * {price}"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Use column keys in curly braces, e.g., {'{'}qty{'}'} * {'{'}price{'}'}
                  </p>
                </div>
              )}

              {/* Number Format Configuration for Number/Calculated Columns */}
              {(editingColumn.column.type === 'number' || editingColumn.column.type === 'calculated') && (
                <div className="space-y-3 p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Number Format
                  </label>
                  
                  {/* Quick Format Presets */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                      Quick Format (presets)
                    </label>
                    <select
                      value={columnNumberFormatPreset}
                      onChange={(e) => setColumnNumberFormatPreset(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    >
                      {Object.values(NUMBER_FORMAT_PRESETS).map(preset => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Custom Format Options */}
                  {columnNumberFormatPreset === 'custom' && (
                    <div className="space-y-3 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Decimal Places
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="10"
                            value={columnNumberFormatCustom.decimals}
                            onChange={(e) => setColumnNumberFormatCustom({
                              ...columnNumberFormatCustom,
                              decimals: parseInt(e.target.value) || 0
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Thousands Separator
                          </label>
                          <label className="flex items-center h-10 px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700">
                            <input
                              type="checkbox"
                              checked={columnNumberFormatCustom.useThousandsSeparator}
                              onChange={(e) => setColumnNumberFormatCustom({
                                ...columnNumberFormatCustom,
                                useThousandsSeparator: e.target.checked
                              })}
                              className="h-4 w-4 text-teal-600 rounded focus:ring-teal-500"
                            />
                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use comma separator</span>
                          </label>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Prefix (e.g., $, ฿)
                          </label>
                          <input
                            type="text"
                            value={columnNumberFormatCustom.prefix}
                            onChange={(e) => setColumnNumberFormatCustom({
                              ...columnNumberFormatCustom,
                              prefix: e.target.value
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            placeholder="e.g., $, ฿"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Suffix (e.g., %, kg)
                          </label>
                          <input
                            type="text"
                            value={columnNumberFormatCustom.suffix}
                            onChange={(e) => setColumnNumberFormatCustom({
                              ...columnNumberFormatCustom,
                              suffix: e.target.value
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            placeholder="e.g., %, kg"
                          />
                        </div>
                      </div>
                      
                      {/* Preview */}
                      <div className="pt-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Preview
                        </label>
                        <div className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white font-mono">
                          {formatNumber(12345.6789, {
                            preset: 'custom',
                            decimals: columnNumberFormatCustom.decimals,
                            useThousandsSeparator: columnNumberFormatCustom.useThousandsSeparator,
                            prefix: columnNumberFormatCustom.prefix,
                            suffix: columnNumberFormatCustom.suffix
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Preview for presets */}
                  {columnNumberFormatPreset !== 'custom' && columnNumberFormatPreset !== 'none' && (
                    <div className="pt-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Preview
                      </label>
                      <div className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white font-mono">
                        {formatNumber(12345.6789, { preset: columnNumberFormatPreset })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {(editingColumn.column.type === 'number' || editingColumn.column.type === 'calculated') && (
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editingColumn.column.showTotal || false}
                      onChange={(e) => {
                        setEditingColumn({
                          ...editingColumn,
                          column: {
                            ...editingColumn.column,
                            showTotal: e.target.checked
                          }
                        });
                      }}
                      className="h-4 w-4 text-teal-600 rounded focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      Show total at bottom of column
                    </span>
                  </label>
                </div>
              )}

              {/* Warning about name changes */}
              {(editingColumn.column.label !== editingColumn.originalColumn.label || 
                editingColumn.column.key !== editingColumn.originalColumn.key) && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>⚠️ Column Name/Key Change Detected:</strong>
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    From: "{editingColumn.originalColumn.label}" ({editingColumn.originalColumn.key})
                    <br />
                    To: "{editingColumn.column.label}" ({editingColumn.column.key})
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-2">
                    Existing data will be preserved under the old key "{editingColumn.originalColumn.key}". 
                    You may need to manually migrate data if you want to move it to the new key.
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingColumn(null);
                  setShowFormulaBuilder(false);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSaveColumnEdit();
                }}
                disabled={!editingColumn.column.label || (editingColumn.column.type === 'calculated' && !editingColumn.column.formula)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Column
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Formula Builder Modal (Separate from Column Edit Modal) */}
      {showFormulaBuilder && editingColumn && (
        <FormulaBuilder
          columns={(localFields.find(f => f.name === editingColumn.fieldName)?.columns || []).filter((_, idx) => idx !== editingColumn.columnIndex)}
          formula={editingColumn.column.formula || ''}
          onChange={(formula) => {
            setEditingColumn({
              ...editingColumn,
              column: {
                ...editingColumn.column,
                formula
              }
            });
            setShowFormulaBuilder(false);
          }}
          onClose={() => setShowFormulaBuilder(false)}
          isDark={document.documentElement.classList.contains('dark')}
        />
      )}
    </div>
  );
};

export default CustomAttributesConfig;

